import { Component, h, Prop, Element, Host } from '@stencil/core';
import { ComponentSize, TextAlignType } from '../../interface';
import { ProgressColor } from './progress-interface';
import { hasSlot } from '../../utils';

/**
 * @slot text - The progress inner text.
 *
 * @part progress - The component's main element.
 * @part progress-bar - The component's bar element.
 * @part progress-text - The component's inner text element.
 */
@Component({
  tag: 'o2-progress',
  styleUrl: 'progress.scss',
  shadow: true,
})
export class O2Progress {
  private hasTextSlot: boolean = false;

  @Element() el: HTMLO2ProgressElement;

  /**
   * Progress height in pixels.
   */
  @Prop() height?: number;

  /**
   * Size of the progress height.
   * Possible values: `"small"`, `"medium"`, `"large"`.
   */
  @Prop() size?: ComponentSize = 'small';

  /**
   * Set actual progress number.
   */
  @Prop() value: number;

  /**
   * Set minimum value of the progress.
   */
  @Prop() min?: number = 0;

  /**
   * Set maximum value of the progress.
   */
  @Prop() max?: number = 100;

  /**
   * Color of the progress.
   * Possible values: `"primary"`, `"success"`, `"warning"`, `"danger"`, `"yellow"`, `"cyan"`.
   */
  @Prop() color: ProgressColor = 'primary';

  /**
   * If set to false, border-radius property is 0. Useful for assembling multiple progressbars.
   */
  @Prop() borderRadiusLeft?: boolean = true;

  /**
   * If set to false, border-radius property is 0. Useful for assembling multiple progressbars.
   */
  @Prop() borderRadiusRight?: boolean = true;

  /**
   * Inner text.
   */
  @Prop() text?: string;

  /**
   * Inner text align. Default to center.
   */
  @Prop() textAlign?: TextAlignType = 'center';

  private setHeight() {
    const style: { height?: string } = {};

    if (this.height) {
      style.height = `${this.height}px`;
    }

    return style;
  }

  private calculatePercentage(): number {
    const percentage = ((this.value - this.min) / (this.max - this.min)) * 100;

    return +percentage.toFixed(1);
  }

  private setProgress() {
    const percentage = this.calculatePercentage();

    return {
      width: `${percentage}%`,
    };
  }

  private isMoreThanHalfProgress(): boolean {
    const half = (this.max - this.min) / 2;

    if (this.value >= half) {
      return true;
    }

    return false;
  }

  connectedCallback() {
    this.hasTextSlot = hasSlot(this.el, '[slot="text"]');
  }

  render() {
    const hasText = !!this.text || this.hasTextSlot;

    return (
      <Host role="progressbar">
        <div
          class={`progress ${this.size && !this.height ? `progress--${this.size}` : ''} ${!this.borderRadiusLeft ? 'progress--radius-left-0' : ''} ${
            !this.borderRadiusRight ? 'progress--radius-right-0' : ''
          }`}
          style={this.setHeight()}
          part="progress"
        >
          <div
            class={`progress-bar progress-bar--${this.color} ${this.value == 0 ? 'progress-bar--empty' : ''}`}
            style={this.setProgress()}
            aria-valuenow={this.value}
            aria-valuemin={this.min}
            aria-valuemax={this.max}
            part="progress-bar"
          ></div>

          {hasText && (
            <div
              class={`progress-bar__text ${this.isMoreThanHalfProgress() ? 'progress-bar__text--light' : 'progress-bar__text--dark'} progress-bar__text--${this.textAlign}`}
              part="progress-text"
            >
              {this.text ? this.text : ''}
              {this.hasTextSlot ? <slot name="text"></slot> : ''}
            </div>
          )}
        </div>
      </Host>
    );
  }
}
